import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { API } from '../utils/helpers';
import { parseHeight, heightLabel } from '../utils/height';

// ── Rentals and demos on the public booking page (4 Oct) ──────────────────────
//
// The order is the other way round from service: dates first, so the customer
// only ever picks from bikes free for the whole trip. Then a model and a size
// per rider (v2: one card per model, sizes inside with the rider heights they
// fit; the server gives out a free bike of that size), each rider's answers,
// contact details, and the deposit if the shop takes one. The damage hold is
// placed at pick-up, not here.
//
// Rendered inside BookingPage's frame; it passes its colours (C), the shop
// slug, the Stripe key and its own PaymentForm.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const addDays = (s, n) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); };
const weekday = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
const dayCount = (a, b) => { const f = (s) => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); }; return Math.round((f(b) - f(a)) / 86400000) + 1; };
const label = (s, long) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: long ? 'long' : 'short', month: long ? 'long' : 'short', day: 'numeric' }); };
const clock = (hhmm) => { const [h, m] = String(hhmm || '').split(':').map(Number); if (!Number.isFinite(h)) return ''; return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`; };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const DAYS3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function RentalBooking({ C, shop, cfg, paymentMode, stripePublishableKey, PaymentForm, input, StepBadge }) {
  const rules = cfg.rules;
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [avail, setAvail] = useState(null);       // { days, bikes: [{id, free, why}] } or { error }
  const [picked, setPicked] = useState([]);       // [{ key, modelId, size }], one per bike, in the order picked
  const [riders, setRiders] = useState({});       // key -> { name, answers }
  const [height, setHeight] = useState('');
  const [category, setCategory] = useState('');
  const [address, setAddress] = useState('');
  // A link to one model (?model=<id>, copied in Settings) shows that model
  // first, with a way to see the rest.
  const linkedModel = useMemo(() => { const id = new URLSearchParams(window.location.search).get('model'); return (cfg.models || []).some(m => m.id === id) ? id : null; }, [cfg.models]);
  const [showAll, setShowAll] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [payment, setPayment] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [done, setDone] = useState(null);
  const [reload, setReload] = useState(0);         // bumped to re-ask which bikes are free

  // Dates that can be a pick-up or return day.
  const first = addDays(cfg.today, rules.noticeDays);
  const last = addDays(cfg.today, rules.windowDays);
  const usable = (d) => d >= first && d <= last && rules.openDays.includes(weekday(d)) && !(rules.blackoutDates || []).includes(d);

  const months = useMemo(() => {
    const out = [];
    let cur = first.slice(0, 7);
    while (cur <= last.slice(0, 7)) {
      const [y, m] = cur.split('-').map(Number);
      out.push({ y, m, firstDow: new Date(y, m - 1, 1).getDay(), dim: new Date(y, m, 0).getDate() });
      const nx = new Date(y, m, 1);
      cur = `${nx.getFullYear()}-${String(nx.getMonth() + 1).padStart(2, '0')}`;
    }
    return out.slice(0, 2);
  }, [first, last]);

  const pickDay = (d) => {
    if (!start || (start && end)) { setStart(d); setEnd(null); setAvail(null); return; }
    if (d < start) { setStart(d); return; }
    setEnd(d);
  };

  useEffect(() => {
    if (!start || !end) return;
    let live = true;
    setAvail(null);
    fetch(`${API}/book/rent/availability?shop=${encodeURIComponent(shop)}&start=${start}&end=${end}`)
      .then(r => r.json()).then(d => {
        if (!live) return;
        setAvail(d);
        // Keep what still fits in what is free for the new dates.
        if (d.bikes) setPicked(p => {
          const left = {};
          for (const b of cfg.fleet) if ((d.bikes.find(x => x.id === b.id) || {}).free) left[`${b.modelId}|${b.size}`] = (left[`${b.modelId}|${b.size}`] || 0) + 1;
          return p.filter(x => (left[`${x.modelId}|${x.size}`] = (left[`${x.modelId}|${x.size}`] || 0) - 1) >= 0);
        });
      }).catch(() => { if (live) setAvail({ error: 'Could not check the bikes. Please try again.' }); });
    return () => { live = false; };
  }, [start, end, shop, reload, cfg.fleet]);

  const days = start && end ? dayCount(start, end) : 0;
  const models = cfg.models || [];
  const modelOf = (id) => models.find(m => m.id === id) || {};
  const charge = (avail && avail.charge) || { days, chargeDays: days, weekend: false };
  const returnTime = (avail && avail.returnTime) || rules.returnTime;
  // What one bike of a model costs for these dates (server/helpers/rentals.js
  // pricing): by the day; on the shop's weekend, with the model's weekend fee
  // added on top, or (mode replace) the weekend price instead of the days.
  const addon = !rules.weekend || rules.weekend.mode !== 'replace';
  const lineFor = (m) => {
    const wk = charge.weekend && m.weekendRate != null;
    if (wk && !addon) return { weekend: true, fee: 0, qty: 1, unit: m.weekendRate, amount: m.weekendRate };
    const fee = wk ? m.weekendRate : 0;
    return { weekend: wk, fee, qty: charge.chargeDays, unit: m.rate, amount: m.rate * charge.chargeDays + fee };
  };
  const dayWord = (n) => `${n} day${n === 1 ? '' : 's'}`;
  const lines = picked.map(p => ({ ...p, m: modelOf(p.modelId), ...lineFor(modelOf(p.modelId)) }));
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const deposit = Math.min(total, rules.depositMode === 'full' ? total : rules.depositMode === 'flat' ? rules.depositFlat * lines.length : lines.reduce((s, l) => s + l.unit, 0));
  const hold = lines.reduce((s, l) => s + (l.m.hold || 0), 0);
  const takesPayment = paymentMode && paymentMode !== 'off' && deposit > 0;
  const charged = days > rules.chargeOverDays;
  const inches = parseHeight(height);
  const fitsYou = (z) => inches != null && z.heightMin != null && z.heightMax != null && inches >= z.heightMin && inches <= z.heightMax;
  // Bikes of a model and size free for all of these dates.
  const freeOf = (modelId, size) => cfg.fleet.filter(b => b.modelId === modelId && b.size === size && ((avail && avail.bikes && avail.bikes.find(x => x.id === b.id)) || {}).free).length;
  const outOn = (modelId, size) => {
    const out = cfg.fleet.filter(b => b.modelId === modelId && b.size === size).map(b => ((avail && avail.bikes && avail.bikes.find(x => x.id === b.id)) || {}).why).filter(Boolean);
    const d = out.length ? String(out[0]).replace('Out ', '') : '';
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `Out ${label(d)}` : 'Not free';
  };
  const countOf = (modelId, size) => picked.filter(p => p.modelId === modelId && p.size === size).length;
  const addPick = (modelId, size) => {
    const key = `${modelId}|${size}|${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
    setPicked(p => [...p, { key, modelId, size }]);
    const hq = (cfg.questions || []).find(q => q.id === 'height');
    if (hq && height.trim() && !picked.length) setRiders(all => ({ ...all, [key]: { name: name, answers: { height: height.trim() } } }));
  };
  const dropPick = (modelId, size) => setPicked(p => { const i = p.map(x => `${x.modelId}|${x.size}`).lastIndexOf(`${modelId}|${size}`); return i < 0 ? p : p.filter((_, j) => j !== i); });

  const body = (paymentIntentId) => ({
    shop, start, end, customerName: name.trim(), customerPhone: phone.trim(), customerEmail: email.trim(), notes: notes.trim(),
    ...(rules.askAddress ? { address: address.trim() } : {}),
    bikes: picked.map(p => ({ modelId: p.modelId, size: p.size, rider: riders[p.key] || {} })),
    ...(paymentIntentId ? { paymentIntentId } : {}),
  });

  const create = async (paymentIntentId = null) => {
    try {
      const r = await fetch(`${API}/book/rent/create`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body(paymentIntentId)) });
      const d = await r.json();
      if (!r.ok) {
        setPayment(null); setStripePromise(null);
        setError(d.error || 'Something went wrong. Please call the shop.');
        if (r.status === 409) setReload(n => n + 1);
      } else { setDone({ ...d.booking, paid: paymentIntentId ? payment : null }); window.scrollTo(0, 0); }
    } catch (_) { setError('Something went wrong. Please call the shop.'); }
    setBusy(false);
  };

  const submit = async () => {
    setError('');
    if (!start || !end) return setError('Pick your pick-up and return days.');
    if (!picked.length) return setError('Choose at least one bike.');
    if (!name.trim()) return setError('Please enter your name.');
    if (!phone.trim() && !email.trim()) return setError('Please enter a phone number or email so we can reach you.');
    setBusy(true);
    if (takesPayment) {
      try {
        const r = await fetch(`${API}/book/rent/payment-intent`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shop, start, end, bikes: picked.map(p => ({ modelId: p.modelId, size: p.size })) }) });
        const d = await r.json();
        if (!r.ok) { setError(d.error || 'Could not start payment.'); setBusy(false); return; }
        if (d.paymentRequired && d.clientSecret && stripePublishableKey) {
          setStripePromise(loadStripe(stripePublishableKey, { stripeAccount: d.stripeAccountId }));
          setPayment(d); setBusy(false); window.scrollTo(0, 0);
          return;
        }
      } catch (_) { /* fall through to a booking without payment */ }
    }
    await create();
  };

  const section = (n, text, active) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '22px 0 12px' }}>
      <StepBadge n={n} active={active} /><span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{text}</span>
    </div>
  );

  if (done) return (
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#eafaf1', color: C.ok, fontSize: 26, lineHeight: '52px', margin: '0 auto 14px' }}>✓</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 6 }}>You're booked!</div>
      <div style={{ fontSize: 14, color: C.sub, lineHeight: 1.6, marginBottom: 18 }}>
        {done.bikes.join(', ')}<br /><strong style={{ color: C.text }}>{done.whenText}</strong>
      </div>
      <div style={{ textAlign: 'left', background: C.bg, borderRadius: 10, padding: '14px 16px', fontSize: 13, color: C.sub, lineHeight: 1.7 }}>
        <strong style={{ color: C.text }}>At pick-up</strong><br />
        Come from {clock(rules.pickupTime)} on your first day with a photo ID and the card you booked with.
        {done.hold > 0 && (done.holdMode === 'charge'
          ? ` We charge a ${money(done.hold)} damage deposit when the bikes go out and refund it when they come back.`
          : ` We place a ${money(done.hold)} damage hold on it when the bikes go out, released when they come back.`)}
        {' '}Bring them back by {clock(done.returnTime || rules.returnTime)} on your last day. The link in your confirmation lets you cancel.
      </div>
    </div>
  );

  if (payment && stripePromise) return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>Confirm and pay</div>
      <div style={{ fontSize: 13, color: C.sub, marginBottom: 16 }}>{lines.length} bike{lines.length === 1 ? '' : 's'} · {label(start)} to {label(end)}</div>
      <Elements stripe={stripePromise} options={{ clientSecret: payment.clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: C.accent } } }}>
        <PaymentForm amount={payment.amount} captureMethod={payment.captureMethod} busy={busy} forRental
          onPaid={(id) => { setBusy(true); create(id); }} onBack={() => { setPayment(null); setStripePromise(null); }} />
      </Elements>
      {error && <div style={{ fontSize: 13, color: C.danger, marginTop: 10 }}>{error}</div>}
    </div>
  );

  return (
    <>
      {section(1, 'Pick up and return', !end)}
      <div style={{ fontSize: 12.5, color: C.sub, margin: '-4px 0 10px' }}>
        {!start ? 'Tap your pick-up day, then your return day.' : !end ? 'Now tap your return day.' : `${label(start)} to ${label(end)} · ${days} day${days === 1 ? '' : 's'}. Pick up from ${clock(rules.pickupTime)}, back by ${clock(returnTime)}.`}
        {end && charge.weekend && models.some(m => m.weekendRate != null) && (
          <div style={{ display: 'inline-block', marginTop: 6, fontSize: 12, fontWeight: 600, color: addon ? C.sub : C.ok, background: addon ? C.bg : '#eafaf1', borderRadius: 6, padding: '3px 8px' }}>{addon ? 'Weekend: a weekend fee applies' : 'Weekend rate applies'}</div>
        )}
        {end && !charge.weekend && charge.chargeDays !== days && (
          <div style={{ marginTop: 4, color: C.ok, fontWeight: 600 }}>Charged as {charge.chargeDays} day{charge.chargeDays === 1 ? '' : 's'}.</div>
        )}
      </div>
      {months.map(({ y, m, firstDow, dim }) => (
        <div key={`${y}-${m}`} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{MONTHS[m - 1]} {y}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
            {DOW.map((d, i) => <span key={i} style={{ fontSize: 11, color: C.muted, textAlign: 'center' }}>{d}</span>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px 0' }}>
            {Array.from({ length: firstDow }).map((_, i) => <span key={`p${i}`} />)}
            {Array.from({ length: dim }).map((_, i) => {
              const key = `${y}-${String(m).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
              const isEdge = key === start || key === end;
              const inRange = start && end && key > start && key < end;
              const ok = usable(key);
              const base = { fontSize: 13, textAlign: 'center', padding: '8px 0', fontVariantNumeric: 'tabular-nums' };
              if (isEdge) return <span key={key} onClick={() => pickDay(key)} style={{ ...base, background: C.accent, color: '#fff', fontWeight: 700, borderRadius: 8, cursor: 'pointer' }}>{i + 1}</span>;
              if (inRange) return <span key={key} style={{ ...base, background: C.accentTint.replace(/14$/, '33'), color: C.text, fontWeight: 600 }}>{i + 1}</span>;
              if (ok) return <span key={key} onClick={() => pickDay(key)} style={{ ...base, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, cursor: 'pointer', margin: '0 2px' }}>{i + 1}</span>;
              return <span key={key} style={{ ...base, color: C.disabled }}>{i + 1}</span>;
            })}
          </div>
        </div>
      ))}

      {section(2, 'Choose your bikes', !!end && !picked.length)}
      {!end && <div style={{ fontSize: 13, color: C.muted }}>Pick your dates first, and we'll show what's free.</div>}
      {end && !avail && <div style={{ fontSize: 13, color: C.sub }}>Checking which bikes are free…</div>}
      {avail && avail.error && <div style={{ fontSize: 13, color: C.danger }}>{avail.error}</div>}
      {avail && avail.bikes && (
        <>
          {models.some(m => m.sizes.some(z => z.heightMin != null)) && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontSize: 12.5, color: C.sub }}>Your height</span>
              <input style={{ ...input, width: 120, padding: '7px 10px' }} placeholder={`e.g. 5'9"`} value={height} onChange={e => setHeight(e.target.value)} />
              <span style={{ fontSize: 12, color: C.muted }}>{height.trim() && inches == null ? 'Try 5\'9" or 175 cm' : 'optional: shows which sizes fit you'}</span>
            </div>
          )}
          {(cfg.categories || []).length > 1 && (!linkedModel || showAll) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {['', ...cfg.categories].map(c => (
                <button type="button" key={c || 'all'} onClick={() => setCategory(c)}
                  style={{ fontSize: 12.5, padding: '6px 12px', borderRadius: 16, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${category === c ? C.accent : C.border}`, background: category === c ? C.accentTint : '#fff', color: category === c ? C.accent : C.sub, fontWeight: category === c ? 600 : 500 }}>{c || 'All'}</button>
              ))}
            </div>
          )}
          {models.filter(m => (linkedModel && !showAll ? m.id === linkedModel : !category || m.category === category)).map(m => {
            const anyPicked = picked.some(p => p.modelId === m.id);
            const ln = lineFor(m);
            return (
              <div key={m.id} style={{ border: anyPicked ? `2px solid ${C.accent}` : `1px solid ${C.border}`, borderRadius: 12, padding: anyPicked ? 11 : 12, marginBottom: 10, background: '#fff' }}>
                {/* The shop's photo of this model (8 Oct). Contained, not
                    cropped: a bike photographed side-on loses a wheel to a crop. */}
                {m.photo && (
                  <img src={`${API}${m.photo}`} alt={m.name} loading="lazy" decoding="async"
                    style={{ display: 'block', width: '100%', height: 180, objectFit: 'contain', background: '#fff', borderRadius: 8, marginBottom: 10 }} />
                )}
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{m.name}{m.demo && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: C.accentTint, color: C.accent, fontWeight: 600, marginLeft: 8 }}>Demo</span>}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {m.category}{m.demo && rules.demoCreditDays ? `${m.category ? ' · ' : ''}the fee counts toward this bike if you buy within ${rules.demoCreditDays} days` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{money(m.rate).replace('.00', '')}<span style={{ fontSize: 11.5, fontWeight: 500, color: C.muted }}> / day</span></div>
                    {m.weekendRate != null && rules.weekend && rules.weekend.on && (
                      <div style={{ fontSize: 11.5, color: ln.weekend ? C.ok : C.muted, fontWeight: 600 }}>{addon ? `+${money(m.weekendRate).replace('.00', '')} weekend fee` : (rules.weekend.match === 'dueBack' ? `${money(m.weekendRate).replace('.00', '')} for the weekend` : `${money(m.weekendRate).replace('.00', '')} ${DAYS3[rules.weekend.pickupDay]} to ${DAYS3[rules.weekend.returnDay]}`)}</div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {m.sizes.map(z => {
                    const free = freeOf(m.id, z.size);
                    const n = countOf(m.id, z.size);
                    const fit = fitsYou(z);
                    const hText = z.heightMin != null && z.heightMax != null ? `${heightLabel(z.heightMin)}–${heightLabel(z.heightMax)}` : '';
                    if (!free) return (
                      <div key={z.size} style={{ border: `1px dashed ${C.disabled}`, borderRadius: 9, padding: '8px 10px', minWidth: 112, opacity: 0.6 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.muted }}>{z.size || 'One size'}</div>
                        {hText && <div style={{ fontSize: 11.5, color: C.muted }}>{hText}</div>}
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{outOn(m.id, z.size)}</div>
                      </div>
                    );
                    return (
                      <div key={z.size} role="button" tabIndex={0}
                        onClick={() => (n > 0 ? dropPick(m.id, z.size) : addPick(m.id, z.size))}
                        onKeyDown={e => { if (e.key === 'Enter') (n > 0 ? dropPick(m.id, z.size) : addPick(m.id, z.size)); }}
                        style={{ border: n > 0 ? `2px solid ${C.accent}` : `1px solid ${fit ? C.ok : C.border}`, background: n > 0 ? C.accentTint : '#fff', borderRadius: 9, padding: n > 0 ? '7px 9px' : '8px 10px', minWidth: 112, cursor: 'pointer' }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span>{z.size || 'One size'}</span>{n > 0 && <span style={{ color: C.accent }}>{n > 1 ? `×${n}` : '✓'}</span>}
                        </div>
                        {hText && <div style={{ fontSize: 11.5, color: C.sub }}>{hText}</div>}
                        {fit && <div style={{ fontSize: 11, color: C.ok, fontWeight: 600, marginTop: 2 }}>Fits your height</div>}
                        {n > 0 && n < free && (
                          <div onClick={e => { e.stopPropagation(); addPick(m.id, z.size); }} style={{ fontSize: 11, color: C.accent, fontWeight: 600, marginTop: 3 }}>+ another</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {linkedModel && models.length > 1 && (
            <button type="button" onClick={() => setShowAll(v => !v)}
              style={{ background: 'transparent', border: 'none', color: C.sub, fontSize: 12.5, cursor: 'pointer', padding: '2px 0 8px', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}>
              {showAll ? 'Show only this bike' : 'See all bikes'}
            </button>
          )}
          {models.length > 0 && <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', margin: '2px 0' }}>Pick as many bikes as you need, one per rider. Tap a picked size again to remove it.</div>}
        </>
      )}

      {cfg.disclaimerText && (
        <div style={{ background: '#faf9f5', border: `1px solid ${C.borderSoft || C.border}`, borderRadius: 10, padding: '10px 14px', margin: '10px 0 6px' }}>
          <span style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{cfg.disclaimerText}</span>
        </div>
      )}

      {section(3, 'Who is riding', picked.length > 0)}
      {!picked.length && <div style={{ fontSize: 13, color: C.muted }}>Each bike's rider goes here.</div>}
      {lines.map(l => {
        const rd = riders[l.key] || { name: '', answers: {} };
        const set = (patch) => setRiders(all => ({ ...all, [l.key]: { ...rd, ...patch } }));
        return (
          <div key={l.key} style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>{l.m.name}{l.size ? ` · ${l.size}` : ''}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input style={input} placeholder="Rider name" value={rd.name} onChange={e => set({ name: e.target.value })} />
              {(cfg.questions || []).filter(q => q.label).map(q => (
                <input key={q.id} style={input} placeholder={q.label} value={(rd.answers || {})[q.id] || ''}
                  onChange={e => set({ answers: { ...(rd.answers || {}), [q.id]: e.target.value } })} />
              ))}
            </div>
          </div>
        );
      })}

      {section(4, 'Your info', picked.length > 0)}
      <input style={{ ...input, marginBottom: 8 }} placeholder="Full name" value={name} onChange={e => setName(e.target.value)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <input style={input} placeholder="Phone" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} />
        <input style={input} placeholder="Email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} />
      </div>
      {rules.askAddress && <input style={{ ...input, marginBottom: 8 }} placeholder="Home address" autoComplete="street-address" value={address} onChange={e => setAddress(e.target.value)} />}
      <textarea style={{ ...input, height: 56, resize: 'none', marginBottom: 14 }} placeholder="Anything we should know?" value={notes} onChange={e => setNotes(e.target.value)} />

      {lines.length > 0 && days > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginBottom: 12 }}>
          {lines.map(l => (
            <div key={l.key} style={{ padding: '3px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.sub }}>
                <span>{l.m.name}{l.size ? ` · ${l.size}` : ''}, {l.weekend && !l.fee ? (rules.weekend.match === 'dueBack' ? 'weekend rate' : `weekend rate (${DAYS3[rules.weekend.pickupDay]} to ${DAYS3[rules.weekend.returnDay]})`) : `${dayWord(l.qty)} × ${money(l.unit).replace('.00', '')}`}</span>
                <span style={{ color: C.text }}>{money(l.amount - l.fee)}</span>
              </div>
              {l.fee > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.sub, padding: '2px 0 0 10px' }}>
                  <span>Weekend fee</span><span style={{ color: C.text }}>{money(l.fee)}</span>
                </div>
              )}
              {l.weekend && !l.fee && days > 1 && <div style={{ fontSize: 11.5, color: C.muted }}>By the day this would be {dayWord(charge.chargeDays)} × {money(l.m.rate).replace('.00', '')} = {money(l.m.rate * charge.chargeDays)}.</div>}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: C.text, padding: '6px 0 2px' }}><span>{lines.every(l => l.m.demo) ? 'Demo total' : 'Rental total'}</span><span>{money(total)}</span></div>
          {takesPayment && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 600, color: C.accent, padding: '2px 0 6px' }}>
            <span>Due now: {rules.depositMode === 'full' ? 'the rental' : rules.depositMode === 'flat' ? 'deposit' : lines.every(l => l.weekend && !l.fee) ? 'the weekend rate' : "first day's rental"}</span><span>{money(deposit)}</span></div>}
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>
            {takesPayment && deposit < total ? 'The rest is due at pick-up. ' : !takesPayment ? 'Payment is due at pick-up. ' : ''}
            {hold > 0 && (charged
              ? `At pick-up we charge a ${money(hold)} damage deposit, refunded when the bikes come back. `
              : `At pick-up we place a ${money(hold)} hold on your card for damage, released when the bikes come back. `)}
            Cancel up to {rules.freeCancelHours} hours before pick-up{takesPayment ? ' for a full refund' : ''}.
          </div>
        </div>
      )}
      {error && <div style={{ fontSize: 13, color: C.danger, marginBottom: 10 }}>{error}</div>}
      <button onClick={submit} disabled={busy}
        style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', padding: 14, borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'inherit' }}>
        {busy ? 'Booking…' : takesPayment ? 'Continue to payment' : 'Book the bikes'}
      </button>
    </>
  );
}
